import React, { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import AddTaxRegistrationModal from "src/main/MarinaTaxes/AddTaxRegistrationModal"

import Button from "src/components/Button"
import DataTable from "src/components/DataTable"

import {
  deleteTaxRegistration,
  getTaxRegistrations,
} from "src/api/Manage/Taxes"

import { useToast } from "src/hooks/use_toast"

export default function TaxRegistrationsSection() {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const [addTaxRegistrationModalOpen, setAddTaxRegistrationModalOpen] =
    useState(false)

  const { data: taxRegistrationsData, isLoading } = useQuery({
    queryKey: ["marina", marinaSlug, "taxRegistrations"],
    queryFn: () => getTaxRegistrations(marinaSlug),
  })

  const { mutate: deleteTaxRegistrationMutation } = useMutation({
    mutationKey: ["marina", marinaSlug, "deleteTaxRegistration"],
    mutationFn: (taxRegistrationId) =>
      deleteTaxRegistration(marinaSlug, taxRegistrationId),
    onSuccess: ({ data: { name, identifier } }) => {
      showToast(
        `Tax Registration "${identifier}" for ${name} has been removed`,
        {
          type: "success",
        }
      )
      // Invalidate queries so the tax registrations table and available taxes dropdown update
      queryClient.invalidateQueries(["marina", marinaSlug, "taxRegistrations"])
      queryClient.invalidateQueries(["marina", marinaSlug, "marinaTaxes"])
    },
    onError: (error) => {
      showToast(error.message, { type: "error" })
    },
  })

  const rowConfig = {
    actions: (taxRegistration) => createActions(taxRegistration),
  }

  const createActions = (taxRegistration) => {
    return [
      {
        action: "Delete Tax Registration",
        onClick: () => handleDelete(taxRegistration),
      },
    ]
  }

  const handleDelete = (taxRegistration) => {
    const { id, name, identifier } = taxRegistration
    if (
      window.confirm(
        `Are you sure you want to delete the tax registration "${identifier}" for ${name}?\n\nThis registration number will no longer show up on newly generated receipts.`
      )
    ) {
      deleteTaxRegistrationMutation(id)
    }
  }

  const colDefs = [
    {
      key: "name",
      header: "Tax Name",
    },
    {
      key: "taxTypeDisplay",
      header: "Type of Tax",
    },
    {
      key: "identifier",
      header: "Tax Registration",
    },
  ]

  const handleAddTaxRegistrationClick = () => {
    setAddTaxRegistrationModalOpen(true)
  }

  const handleCloseModal = () => {
    setAddTaxRegistrationModalOpen(false)
  }

  return (
    <>
      <div className="my-4 flex items-center justify-between px-2">
        <h2 className="m-0 text-2xl font-bold">Tax Registration Numbers</h2>
        <Button variant="primary" onClick={handleAddTaxRegistrationClick}>
          Add Tax Registration
        </Button>
      </div>
      <AddTaxRegistrationModal
        isOpen={addTaxRegistrationModalOpen}
        onClose={handleCloseModal}
        existingTaxRegistrations={taxRegistrationsData?.data || []}
      />
      <div className="grid p-2">
        <DataTable
          name="taxRegistrations"
          rowData={taxRegistrationsData?.data}
          rowConfig={rowConfig}
          colDefs={colDefs}
          isLoading={isLoading}
          showBlankRows={isLoading}
          rowCount={2}
          fullHeight
          autoColumnWidth
        />
      </div>
      <div className="mt-4 px-2 text-sm text-gray-600">
        Tax registration numbers are associated with individual taxes and will
        appear on receipts and invoices.
      </div>
    </>
  )
}
